import moment from "moment"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useHistory, useLocation, useParams } from "react-router-dom"
import _get from "lodash/get"

import PaginationNew from "./components/PaginationNew"
import {
	mainContainerStyle,
	headerSectionStyle,
	headerTitleStyle,
	secondaryButtonStyle,
	tableStyle,
	thStyle,
	tdStyle,
} from "./styles/commonStyles"
import { getMemberVoteDetail, getMemberVotePaymentLogs, getMemberVoteSmsLogs } from "./service"

const DEFAULT_TAB = "info"
const TAB_CONFIG = [
	{ key: "info", label: "ข้อมูลสมาชิก" },
	{ key: "payment", label: "ประวัติการชำระเงิน" },
	{ key: "sms", label: "ประวัติการส่ง SMS" },
]

const tabContainerStyle = {
	display: "flex",
	gap: "12px",
	borderBottom: "1px solid #e5e7eb",
	marginBottom: "24px",
	flexWrap: "wrap",
}

const tabButtonStyle = (isActive) => ({
	border: "none",
	background: "transparent",
	padding: "12px 20px",
	fontSize: "0.95rem",
	fontWeight: isActive ? 600 : 500,
	color: isActive ? "#1f2937" : "#6b7280",
	borderBottom: `2px solid ${isActive ? "#2563eb" : "transparent"}`,
	cursor: "pointer",
	transition: "all 0.2s ease",
	borderRadius: "6px 6px 0 0",
})

const backButtonStyle = {
	...secondaryButtonStyle,
	display: "inline-flex",
	alignItems: "center",
	gap: "8px",
	padding: "0 16px",
	height: "38px",
}

const cardStyle = {
	background: "#ffffff",
	borderRadius: "12px",
	padding: "20px",
	boxShadow: "0 10px 20px -15px rgba(15, 23, 42, 0.25)",
	border: "1px solid #e5e7eb",
}

const infoGridStyle = {
	display: "grid",
	gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
	gap: "16px",
}

const infoItemStyle = {
	display: "flex",
	flexDirection: "column",
	gap: "6px",
}

const infoLabelStyle = {
	fontSize: "0.75rem",
	color: "#6b7280",
	fontWeight: 500,
}

const infoValueStyle = {
	fontSize: "0.95rem",
	color: "#111827",
	fontWeight: 500,
	wordBreak: "break-word",
}

const tableWrapperStyle = {
	overflowX: "auto",
	width: "100%",
	marginTop: "16px",
}

const detailTableStyle = {
	...tableStyle,
	minWidth: "900px",
}

const emptyStateStyle = {
	padding: "24px",
	textAlign: "center",
	color: "#6b7280",
	fontSize: "0.95rem",
}

const statusBadgeStyle = (status) => {
	const baseStyle = {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		padding: "4px 10px",
		borderRadius: "9999px",
		fontSize: "0.75rem",
		fontWeight: 600,
		textTransform: "capitalize",
	}

	switch (status) {
		case "success":
			return { ...baseStyle, background: "#dcfce7", color: "#15803d" }
		case "failed":
		case "error":
			return { ...baseStyle, background: "#fee2e2", color: "#b91c1c" }
		case "expired":
			return { ...baseStyle, background: "#fef3c7", color: "#b45309" }
		default:
			return { ...baseStyle, background: "#e0f2fe", color: "#0369a1" }
	}
}

const errorMessageStyle = {
	background: "#fee2e2",
	border: "1px solid #fecaca",
	color: "#b91c1c",
	padding: "12px 16px",
	borderRadius: "8px",
	marginBottom: "16px",
}

const formatDateTime = (value) => {
	if (!value) return "-"
	return moment(value).format("DD/MM/YYYY HH:mm")
}

const formatCurrency = (value) => {
	if (typeof value !== "number") return "-"
	return new Intl.NumberFormat("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
}

const formatPaymentStatus = (status) => {
	if (!status) return "-"
	const mapping = {
		waiting: "รอดำเนินการ",
		success: "สำเร็จ",
		failed: "ล้มเหลว",
		expired: "หมดอายุ",
	}
	return mapping[status] || status
}

const formatPaymentMethod = (method) => {
	if (!method) return "-"
	const mapping = {
		promptpay: "PromptPay",
		credit: "บัตรเครดิต",
		credit_card: "บัตรเครดิต",
		card: "บัตรเครดิต",
		truemoney: "TrueMoney Wallet",
		internet_banking: "Internet Banking",
	}
	return mapping[method] || method
}

const pickFirst = (...values) => values.find((item) => item) || ""

const extractMemberDetail = (payload) => {
	if (!payload) return null
	if (Array.isArray(payload)) return payload[0] || null
	if (payload.member) return payload.member
	if (payload.memberVote) return payload.memberVote
	if (payload.data && !Array.isArray(payload.data)) return payload.data
	return payload
}

const MemberVoteDetail = () => {
	const { id } = useParams()
	const location = useLocation()
	const history = useHistory()

	const [activeTab, setActiveTab] = useState(() => {
		const hashTab = location?.hash?.replace("#", "")
		const validTabs = TAB_CONFIG.map((tab) => tab.key)
		return validTabs.includes(hashTab) ? hashTab : DEFAULT_TAB
	})
	const [memberDetail, setMemberDetail] = useState(() => location.state?.member || null)
	const [detailLoading, setDetailLoading] = useState(false)
	const [detailError, setDetailError] = useState("")

	const [paymentLogs, setPaymentLogs] = useState([])
	const [paymentPage, setPaymentPage] = useState(1)
	const [paymentLimit, setPaymentLimit] = useState(10)
	const [paymentTotal, setPaymentTotal] = useState(0)
	const [paymentTotalPages, setPaymentTotalPages] = useState(1)
	const [paymentLoading, setPaymentLoading] = useState(false)
	const [paymentError, setPaymentError] = useState("")

	const [smsLogs, setSmsLogs] = useState([])
	const [smsPage, setSmsPage] = useState(1)
	const [smsLimit, setSmsLimit] = useState(10)
	const [smsTotal, setSmsTotal] = useState(0)
	const [smsTotalPages, setSmsTotalPages] = useState(1)
	const [smsLoading, setSmsLoading] = useState(false)
	const [smsError, setSmsError] = useState("")

	const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])

	const resolvedMember = useMemo(() => extractMemberDetail(memberDetail), [memberDetail])

	const resolvedEmail = useMemo(
		() =>
			pickFirst(
				searchParams.get("email"),
				_get(resolvedMember, "memberInfo.email"),
				_get(resolvedMember, "user.email"),
				_get(resolvedMember, "email"),
			),
		[resolvedMember, searchParams],
	)

	const resolvedPhone = useMemo(
		() =>
			pickFirst(
				searchParams.get("phone"),
				_get(resolvedMember, "memberInfo.msisdn"),
				_get(resolvedMember, "memberInfo.phone"),
				_get(resolvedMember, "user.msisdn"),
				_get(resolvedMember, "smsDetail.msisdn"),
			),
		[resolvedMember, searchParams],
	)

	useEffect(() => {
		const hashTab = location?.hash?.replace("#", "")
		const validTabs = TAB_CONFIG.map((tab) => tab.key)
		if (hashTab && validTabs.includes(hashTab) && hashTab !== activeTab) {
			setActiveTab(hashTab)
		}
	}, [location.hash, activeTab])

	useEffect(() => {
		if (!id) return
		let isCancelled = false

		const fetchDetail = async () => {
			setDetailLoading(true)
			setDetailError("")
			try {
				const response = await getMemberVoteDetail(id)
				if (isCancelled) return

				if (response.status === 200 && response.data?.status === 200) {
					const payload = response.data?.data ?? response.data?.member
					const extracted = extractMemberDetail(payload)
					if (extracted) {
						setMemberDetail((prev) => {
							if (!prev) return extracted
							return { ...prev, ...extracted }
						})
					}
				} else if (response.data?.message) {
					setDetailError(response.data.message)
				} else {
					setDetailError("ไม่สามารถโหลดข้อมูลผู้เล่นได้")
				}
			} catch (error) {
				if (isCancelled) return
				console.error("fetchMemberVoteDetail error", error)
				setDetailError("ไม่สามารถโหลดข้อมูลผู้เล่นได้")
			} finally {
				if (!isCancelled) {
					setDetailLoading(false)
				}
			}
		}

		fetchDetail()

		return () => {
			isCancelled = true
		}
	}, [id])

	const fetchPaymentLogs = useCallback(
		async (page = paymentPage, limit = paymentLimit) => {
			if (!id) return

			setPaymentLoading(true)
			setPaymentError("")
			try {
				const params = {
					page,
					limit,
				}
				if (resolvedEmail) params.email = resolvedEmail
				if (resolvedPhone) params.msisdn = resolvedPhone

				const response = await getMemberVotePaymentLogs(id, params)
				if (response.status === 200 && response.data?.status === 200) {
					const payload = response.data?.data || {}
					const items = payload.paymentLogs || payload.items || payload.data || []
					const totalItems = payload.total ?? payload.totalRecords ?? items.length ?? 0
					const totalPagesFromPayload = payload.totalPages || Math.max(1, Math.ceil(totalItems / limit))
					const currentPage = payload.currentPage || page

					setPaymentLogs(Array.isArray(items) ? items : [items])
					setPaymentTotal(totalItems)
					setPaymentTotalPages(totalPagesFromPayload)
					setPaymentPage(currentPage)
					setPaymentLimit(limit)
				} else {
					setPaymentLogs([])
					setPaymentTotal(0)
					setPaymentTotalPages(1)
					setPaymentError(response.data?.message || "ไม่สามารถโหลดประวัติการชำระเงินได้")
				}
			} catch (error) {
				console.error("fetchPaymentLogs error", error)
				setPaymentLogs([])
				setPaymentTotal(0)
				setPaymentTotalPages(1)
				setPaymentError("ไม่สามารถโหลดประวัติการชำระเงินได้")
			} finally {
				setPaymentLoading(false)
			}
		},
		[id, resolvedEmail, resolvedPhone, paymentLimit, paymentPage],
	)

	const fetchSmsLogs = useCallback(
		async (page = smsPage, limit = smsLimit) => {
			if (!id) return

			setSmsLoading(true)
			setSmsError("")
			try {
				const params = {
					page,
					limit,
				}
				if (resolvedEmail) params.email = resolvedEmail
				if (resolvedPhone) params.msisdn = resolvedPhone

				const response = await getMemberVoteSmsLogs(id, params)
				if (response.status === 200 && response.data?.status === 200) {
					const payload = response.data?.data || {}
					const items = payload.smsLogs || payload.items || payload.data || []
					const totalItems = payload.total ?? payload.totalRecords ?? items.length ?? 0
					const totalPagesFromPayload = payload.totalPages || Math.max(1, Math.ceil(totalItems / limit))
					const currentPage = payload.currentPage || page

					setSmsLogs(Array.isArray(items) ? items : [items])
					setSmsTotal(totalItems)
					setSmsTotalPages(totalPagesFromPayload)
					setSmsPage(currentPage)
					setSmsLimit(limit)
				} else {
					setSmsLogs([])
					setSmsTotal(0)
					setSmsTotalPages(1)
					setSmsError(response.data?.message || "ไม่สามารถโหลดประวัติ SMS ได้")
				}
			} catch (error) {
				console.error("fetchSmsLogs error", error)
				setSmsLogs([])
				setSmsTotal(0)
				setSmsTotalPages(1)
				setSmsError("ไม่สามารถโหลดประวัติ SMS ได้")
			} finally {
				setSmsLoading(false)
			}
		},
		[id, resolvedEmail, resolvedPhone, smsLimit, smsPage],
	)

	useEffect(() => {
		if (activeTab === "payment") {
			fetchPaymentLogs(paymentPage, paymentLimit)
		}
	}, [activeTab, fetchPaymentLogs, paymentLimit, paymentPage])

	useEffect(() => {
		if (activeTab === "sms") {
			fetchSmsLogs(smsPage, smsLimit)
		}
	}, [activeTab, fetchSmsLogs, smsLimit, smsPage])

	const handleTabChange = (tabKey) => {
		setActiveTab(tabKey)
		const nextHash = tabKey === DEFAULT_TAB ? "" : `#${tabKey}`
		history.replace({ ...location, hash: nextHash })
	}

	const handleBack = () => {
		if (history.length > 1) {
			history.goBack()
		} else {
			history.push("/campaign-new/member-vote")
		}
	}

	const handlePaymentPageChange = (nextPage) => {
		setPaymentPage(nextPage)
	}

	const handlePaymentLimitChange = (nextLimit) => {
		const parsedLimit = Number(nextLimit) || paymentLimit
		setPaymentPage(1)
		setPaymentLimit(parsedLimit)
	}

	const handleSmsPageChange = (nextPage) => {
		setSmsPage(nextPage)
	}

	const handleSmsLimitChange = (nextLimit) => {
		const parsedLimit = Number(nextLimit) || smsLimit
		setSmsPage(1)
		setSmsLimit(parsedLimit)
	}

	const memberName = useMemo(
		() =>
			pickFirst(
				_get(resolvedMember, "memberInfo.name"),
				_get(resolvedMember, "user.display_name"),
				_get(resolvedMember, "user.name"),
				_get(resolvedMember, "memberInfo.display_name"),
			) || "-",
		[resolvedMember],
	)

	const membershipType = useMemo(() => {
		const value = _get(resolvedMember, "isMembership")
		const userMembership = _get(resolvedMember, "user.isMembership")
		const membership = typeof value === "boolean" ? value : typeof userMembership === "boolean" ? userMembership : undefined
		if (membership === true) return "สมาชิก"
		if (membership === false) return "ทั่วไป"
		return "-"
	}, [resolvedMember])

	const campaignName = useMemo(
		() =>
			pickFirst(
				_get(resolvedMember, "campaignName"),
				_get(resolvedMember, "campaignDetail.campaignName"),
				_get(resolvedMember, "newsVote.setting.name"),
			) || "-",
		[resolvedMember],
	)

	const voteTitle = useMemo(
		() =>
			pickFirst(
				_get(resolvedMember, "campaignDetail.questionTitle"),
				_get(resolvedMember, "vote.title"),
			) || "-",
		[resolvedMember],
	)

	const choiceTitle = useMemo(
		() =>
			pickFirst(
				_get(resolvedMember, "choiceName"),
				_get(resolvedMember, "campaignDetail.choiceTitle"),
				_get(resolvedMember, "choice.title"),
			) || "-",
		[resolvedMember],
	)

	const paymentTypeLabel = useMemo(() => {
		const type = _get(resolvedMember, "paymentType")
		if (type === "payment") return "Payment"
		if (type === "sms") return "SMS"
		return type || "-"
	}, [resolvedMember])

	const packageLabel = useMemo(() => {
		const packageTitle = _get(resolvedMember, "package.title")
		const shortCode = _get(resolvedMember, "package.shortCode")
		const smsPackage = _get(resolvedMember, "smsDetail.packageDetail.title")
		const label = packageTitle || smsPackage || "-"
		return shortCode ? `${label}` : label
	}, [resolvedMember])

	const renderInfoTab = () => {
		if (detailLoading) {
			return <div style={emptyStateStyle}>กำลังโหลดข้อมูล...</div>
		}

		if (detailError) {
			return <div style={errorMessageStyle}>{detailError}</div>
		}

		if (!resolvedMember) {
			return <div style={emptyStateStyle}>ไม่พบข้อมูลผู้เล่น</div>
		}

		return (
			<div style={cardStyle}>
				<div style={infoGridStyle}>
					<div style={infoItemStyle}>
						<span style={infoLabelStyle}>ชื่อผู้เล่น</span>
						<span style={infoValueStyle}>{memberName}</span>
					</div>
					<div style={infoItemStyle}>
						<span style={infoLabelStyle}>อีเมล</span>
						<span style={infoValueStyle}>{resolvedEmail || "-"}</span>
					</div>
					<div style={infoItemStyle}>
						<span style={infoLabelStyle}>เบอร์โทรศัพท์</span>
						<span style={infoValueStyle}>{resolvedPhone || "-"}</span>
					</div>
					<div style={infoItemStyle}>
						<span style={infoLabelStyle}>สถานะสมาชิก</span>
						<span style={infoValueStyle}>{membershipType}</span>
					</div>
					<div style={infoItemStyle}>
						<span style={infoLabelStyle}>แคมเปญ</span>
						<span style={infoValueStyle}>{campaignName}</span>
					</div>
					<div style={infoItemStyle}>
						<span style={infoLabelStyle}>คำถาม</span>
						<span style={infoValueStyle}>{voteTitle}</span>
					</div>
					<div style={infoItemStyle}>
						<span style={infoLabelStyle}>คำตอบที่เลือก</span>
						<span style={infoValueStyle}>{choiceTitle}</span>
					</div>
					<div style={infoItemStyle}>
						<span style={infoLabelStyle}>ประเภทการชำระเงิน</span>
						<span style={infoValueStyle}>{paymentTypeLabel}</span>
					</div>
					<div style={infoItemStyle}>
						<span style={infoLabelStyle}>แพ็กเกจ</span>
						<span style={infoValueStyle}>{packageLabel}</span>
					</div>
					<div style={infoItemStyle}>
						<span style={infoLabelStyle}>จำนวนโหวต</span>
						<span style={infoValueStyle}>{_get(resolvedMember, "quantity") || "-"}</span>
					</div>
					<div style={infoItemStyle}>
						<span style={infoLabelStyle}>Order ID</span>
						<span style={infoValueStyle}>{_get(resolvedMember, "paymentDetail.orderId") || "-"}</span>
					</div>
					<div style={infoItemStyle}>
						<span style={infoLabelStyle}>Message ID (SMS)</span>
						<span style={infoValueStyle}>{_get(resolvedMember, "smsDetail.message_id") || "-"}</span>
					</div>
					<div style={infoItemStyle}>
						<span style={infoLabelStyle}>วันที่สร้าง</span>
						<span style={infoValueStyle}>{formatDateTime(_get(resolvedMember, "createdAt"))}</span>
					</div>
					<div style={infoItemStyle}>
						<span style={infoLabelStyle}>อัปเดตล่าสุด</span>
						<span style={infoValueStyle}>{formatDateTime(_get(resolvedMember, "updatedAt"))}</span>
					</div>
				</div>
			</div>
		)
	}

	const renderPaymentTab = () => {
		return (
			<div style={cardStyle}>
				{paymentError && <div style={errorMessageStyle}>{paymentError}</div>}
				<div style={tableWrapperStyle}>
					<table style={detailTableStyle}>
						<thead>
							<tr>
								{[
									{ key: "createdAt", label: "วันที่ทำรายการ" },
									{ key: "orderId", label: "Order ID" },
									{ key: "status", label: "สถานะ" },
									{ key: "paymentMethod", label: "ช่องทาง" },
									{ key: "quantity", label: "จำนวน" },
									{ key: "price", label: "ยอดรวม (บาท)", align: "right" },
									{ key: "package", label: "แพ็กเกจ" },
								].map((column) => (
									<th key={column.key} style={{ ...thStyle, textAlign: column.align || "left" }}>
										{column.label}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{paymentLoading ? (
								<tr>
									<td colSpan={7} style={emptyStateStyle}>
										กำลังโหลดข้อมูล...
									</td>
								</tr>
							) : paymentLogs.length === 0 ? (
								<tr>
									<td colSpan={7} style={emptyStateStyle}>
										ไม่พบประวัติการชำระเงิน
									</td>
								</tr>
							) : (
								paymentLogs.map((item, index) => (
									<tr key={item?._id || item?.id || index}>
										<td style={tdStyle}>{formatDateTime(item?.createdAt || item?.successAt)}</td>
										<td style={tdStyle}>{item?.orderId || "-"}</td>
										<td style={tdStyle}>
											<span style={statusBadgeStyle(item?.status || "waiting")}>{formatPaymentStatus(item?.status)}</span>
										</td>
										<td style={tdStyle}>{formatPaymentMethod(item?.paymentMethod)}</td>
										<td style={{ ...tdStyle, textAlign: "center" }}>{item?.quantity ?? "-"}</td>
										<td style={{ ...tdStyle, textAlign: "right" }}>{formatCurrency(item?.price)}</td>
										<td style={tdStyle}>{_get(item, "package.title") || "-"}</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
				{paymentTotal > 0 && (
					<PaginationNew
						page={paymentPage}
						totalPages={paymentTotalPages}
						total={paymentTotal}
						limit={paymentLimit}
						onPageChange={handlePaymentPageChange}
						onLimitChange={handlePaymentLimitChange}
					/>
				)}
			</div>
		)
	}

	const renderSmsTab = () => {
		return (
			<div style={cardStyle}>
				{smsError && <div style={errorMessageStyle}>{smsError}</div>}
				<div style={tableWrapperStyle}>
					<table style={detailTableStyle}>
						<thead>
							<tr>
								{[
									{ key: "createdAt", label: "วันที่รับข้อความ" },
									{ key: "messageId", label: "Message ID" },
									{ key: "msisdn", label: "เบอร์โทรศัพท์" },
									{ key: "status", label: "สถานะ" },
									{ key: "choice", label: "คำตอบ" },
									{ key: "package", label: "แพ็กเกจ" },
									{ key: "remark", label: "หมายเหตุ" },
								].map((column) => (
									<th key={column.key} style={{ ...thStyle, textAlign: column.align || "left" }}>
										{column.label}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{smsLoading ? (
								<tr>
									<td colSpan={7} style={emptyStateStyle}>
										กำลังโหลดข้อมูล...
									</td>
								</tr>
							) : smsLogs.length === 0 ? (
								<tr>
									<td colSpan={7} style={emptyStateStyle}>
										ไม่พบประวัติ SMS
									</td>
								</tr>
							) : (
								smsLogs.map((item, index) => {
									const success = item?.success || _get(item, "body.success")
									const messageId = _get(item, "smsDetail.message_id") || _get(item, "body.message_id") || "-"
									const msisdn = _get(item, "smsDetail.msisdn") || _get(item, "body.msisdn") || "-"
									const choice = _get(item, "smsDetail.choiceTitle") || _get(item, "body.choiceTitle") || "-"
									const pkgTitle =
										_get(item, "packageDetail.title") ||
										_get(item, "smsDetail.packageDetail.title") ||
										_get(item, "body.packageDetail.title") ||
										"-"
									const remark = item?.errorMessage || _get(item, "body.errorMessage") || "-"
									return (
										<tr key={item?._id || item?.id || index}>
											<td style={tdStyle}>{formatDateTime(item?.receivedAt || item?.createdAt)}</td>
											<td style={tdStyle}>{messageId}</td>
											<td style={tdStyle}>{msisdn}</td>
											<td style={tdStyle}>
												<span style={statusBadgeStyle(success ? "success" : "failed")}>{success ? "สำเร็จ" : "ไม่สำเร็จ"}</span>
											</td>
											<td style={tdStyle}>{choice}</td>
											<td style={tdStyle}>{pkgTitle}</td>
											<td style={tdStyle}>{remark}</td>
										</tr>
									)
								})
							)}
						</tbody>
					</table>
				</div>
				{smsTotal > 0 && (
					<PaginationNew
						page={smsPage}
						totalPages={smsTotalPages}
						total={smsTotal}
						limit={smsLimit}
						onPageChange={handleSmsPageChange}
						onLimitChange={handleSmsLimitChange}
					/>
				)}
			</div>
		)
	}

	return (
		<div style={mainContainerStyle}>
			<div style={headerSectionStyle}>
				<h2 style={headerTitleStyle}>รายละเอียดผู้เล่น News Vote</h2>
				<button type="button" style={backButtonStyle} onClick={handleBack}>
					กลับไปหน้ารายการ
				</button>
			</div>

			<div style={tabContainerStyle}>
				{TAB_CONFIG.map((tab) => (
					<button key={tab.key} type="button" style={tabButtonStyle(activeTab === tab.key)} onClick={() => handleTabChange(tab.key)}>
						{tab.label}
					</button>
				))}
			</div>

			{activeTab === "info" && renderInfoTab()}
			{activeTab === "payment" && renderPaymentTab()}
			{activeTab === "sms" && renderSmsTab()}
		</div>
	)
}

export default MemberVoteDetail
